import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import {
  useCourtesyVehicles,
  useUpdateCourtesyVehicle,
  type CourtesyVehicle,
} from '@/hooks/useCourtesyVehicles';
import { useNotify } from '@/hooks/useNotify';
import { useAuthStore } from '@/stores/authStore';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import { RegistrationPlate } from '@/components/common/RegistrationPlate';
import { formatCentsForDisplay } from '@/utils/money';
import { describeApiError } from '@/lib/describeApiError';
import { CourtesyVehicleFormDialog } from './CourtesyVehicleFormDialog';

export function CourtesyVehiclesTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManage = hasPermission('equipment.manage');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CourtesyVehicle | null>(null);

  const { data, isLoading, isError } = useCourtesyVehicles();
  const updateMutation = useUpdateCourtesyVehicle();
  const vehicles = data ?? [];

  const handleToggleActive = async (vehicle: CourtesyVehicle, isActive: boolean) => {
    try {
      await updateMutation.mutateAsync({ id: vehicle.id, data: { isActive } });
      notify.success(
        t('courtesy.updated'),
        t('courtesy.updatedDesc', { plate: vehicle.registrationNumber }),
      );
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('courtesy.saveFailed') }));
    }
  };

  const columns: DataTableColumn<CourtesyVehicle>[] = [
    {
      header: t('courtesy.plate'),
      sortKey: 'registrationNumber',
      renderCell: (item) => <RegistrationPlate registrationNumber={item.registrationNumber} />,
    },
    {
      header: t('courtesy.displayName'),
      sortKey: 'displayName',
      renderCell: (item) => (
        <Typography variant="body2">{item.displayName ?? '—'}</Typography>
      ),
    },
    {
      header: t('courtesy.dailyRate'),
      sortKey: 'dailyRateCents',
      renderCell: (item) => (
        <Typography variant="body2">
          {item.dailyRateCents && item.dailyRateCents > 0
            ? formatCentsForDisplay(item.dailyRateCents)
            : t('courtesy.free')}
        </Typography>
      ),
    },
    {
      header: t('courtesy.active'),
      renderCell: (item) => (
        <Switch
          checked={item.isActive}
          disabled={!canManage || updateMutation.isPending}
          onClick={(event) => { event.stopPropagation(); }}
          onChange={(event) => {
            event.stopPropagation();
            void handleToggleActive(item, event.target.checked);
          }}
          slotProps={{ input: { 'aria-label': t('courtesy.activeToggle', { plate: item.registrationNumber }) } }}
        />
      ),
    },
    {
      header: '',
      align: 'right',
      renderCell: (item) =>
        canManage ? (
          <Tooltip title={tc('edit')}>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setEditing(item);
                setDialogOpen(true);
              }}
              aria-label={tc('edit')}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ];

  if (isError) {
    return <Alert severity="error">{t('courtesy.loadFailed')}</Alert>;
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6">{t('courtesy.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('courtesy.description')}
          </Typography>
        </Box>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            {t('courtesy.add')}
          </Button>
        )}
      </Stack>

      <DataTable<CourtesyVehicle>
        columns={columns}
        data={vehicles}
        getRowKey={(item) => item.id}
        {...(canManage
          ? {
              onRowClick: (item: CourtesyVehicle) => {
                setEditing(item);
                setDialogOpen(true);
              },
            }
          : {})}
        sortCompareFn={(a, b, key) => {
          if (key === 'registrationNumber') {
            return a.registrationNumber.localeCompare(b.registrationNumber, 'fi');
          }
          if (key === 'displayName') {
            return (a.displayName ?? '').localeCompare(b.displayName ?? '', 'fi');
          }
          if (key === 'dailyRateCents') {
            return (a.dailyRateCents ?? 0) - (b.dailyRateCents ?? 0);
          }
          return 0;
        }}
        defaultSortKey="registrationNumber"
        loading={isLoading}
        emptyMessage={t('courtesy.empty')}
        ariaLabel={t('courtesy.title')}
        renderMobileCard={(item) => (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <RegistrationPlate registrationNumber={item.registrationNumber} />
              {canManage && (
                <Tooltip title={tc('edit')}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditing(item);
                      setDialogOpen(true);
                    }}
                    aria-label={tc('edit')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Typography variant="body2">{item.displayName ?? '—'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {item.dailyRateCents && item.dailyRateCents > 0
                ? formatCentsForDisplay(item.dailyRateCents)
                : t('courtesy.free')}
            </Typography>
          </Box>
        )}
      />

      <CourtesyVehicleFormDialog
        open={dialogOpen}
        vehicle={editing}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />
    </Box>
  );
}
